import { Injectable } from '@angular/core';
import { CommonRequestService, PagingParam } from './common-request.service';
import { AppSettings } from 'app/app.settings';
import { Gfo } from './gfo.service';
import { UserService } from './user.service';

export interface NagCategory {
	id: number;
	name: string;
	desc: string;
	parentId: number;
	isSpatial: boolean;
	parentCate: string;
}
export interface NagPlaceType {
	id: number;
	name: string;
	desc: string;
}
export interface NagNameAlias {
	id: number;
	name: string;
	stnId: number; // standard name id
	cusId: number;
	cusLevel: number;
	cusNameShort: string;
}

@Injectable({
	providedIn: 'root'
})
export class NagService {

	changedNctNames: string[] = [];

	placeTypes: NagPlaceType[];

	constructor(
		private requestService: CommonRequestService,
		private userService: UserService
	) { }

	getNagCategories(): Promise<NagCategory[]> {
		return new Promise((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'admin/nagCategory';
			this.requestService.requestGet(url, null,
				(rsp) => {
					const nagCates: NagCategory[] = [];
					if (rsp.data) {
						rsp.data.forEach((d) => {
							nagCates.push({
								id: d.nct_id,
								name: d.nct_name,
								desc: d.nct_desc,
								parentId: d.nct_parent_id,
								parentCate: d.parent_category,
								isSpatial: d.nct_is_spatial
							});
						});
					}
					resolve(nagCates);
				}, (err) => {
					reject(err);
				});
		});
	}

	getNagSubCategory(): Promise<NagPlaceType[]> {
		return new Promise((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'admin/nagSubCategory';
			this.requestService.requestGet(url, null, (rsp) => {
				const subs: NagPlaceType[] = [];
				if (rsp.data) {
					rsp.data.forEach((d) => {
						subs.push({
							id: d.nsg_id,
							name: d.nsg_name,
							desc: d.nsg_desc
						});
					});
				}
				this.placeTypes = subs;
				resolve(subs);
			}, (err) => reject(err));
		});
	}

	getNagNamesStandard(cateId: number, stateCode?: string, gfo?: Gfo, paging?: PagingParam): Promise<{
		data: any[],
		misc;
		totalCount?: number
	}> {
		return new Promise((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'admin/nagNamesStandard';
			const params: any = {
				nctId: cateId.toString(),
				customerId: this.userService.getCustomerIdByType()
			}
			if (stateCode) {
				params.stateCode = stateCode;
			}
			if (gfo) {
				params.gfo = JSON.stringify(gfo);
			}
			if (paging) {
				this.requestService.setParamsPaging(params, paging);
			}
			this.requestService.requestGet(url, {
				params: params
			}, (rsp) => {
				resolve({
					data: rsp.data,
					totalCount: rsp.totalCount,
					misc: rsp.misc
				});
			}, (err) => {
				reject(err);
			});
		});
	}

	getNagNamesAlias(standardId: number, cateId: number): Promise<NagNameAlias[]> {
		return new Promise((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'admin/nagNamesAlias';
			this.requestService.requestGet(url, {
				params: {
					stnId: standardId.toString(),
					nctId: cateId.toString(),
					customerId: this.userService.getCustomerIdByType()
				}
			}, (rsp) => {
				const aliasList: NagNameAlias[] = [];
				if (rsp.data) {
					rsp.data.forEach((d) => {
						aliasList.push(({
							id: d.aln_id,
							name: d.aln_name,
							stnId: d.aln_stn_id,
							cusId: d.aln_cus_id,
							cusLevel: d.level,
							cusNameShort: d.cus_name_short
						}));
					});
				}
				resolve(aliasList);
			}, (err) => {
				reject(err);
			});
		});
	}

	// Add a standard name
	addStandard_location(categoryId: number, standardName: string, subCates: string[], parentId?: number): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'admin/nagNamesStandardLocation';
			const body: any = {
				nctId: categoryId,
				stnName: standardName.toUpperCase(),
				subcates: JSON.stringify(subCates),
				customerId: this.userService.getCustomerIdByType()
			};
			if (parentId || parentId === 0) {
				body.stnParentId = parentId;
			}
			this.requestService.requestPost(url, body, () => resolve(), (err) => reject(err));
		});
	}
	addStandard_street(categoryId: number, standardName: string, source: string): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'admin/nagNamesStandardStreet';
			const body: any = {
				nctId: categoryId,
				stnName: standardName.toUpperCase(),
				// source: source,
				customerId: this.userService.getCustomerIdByType()
			};
			if (source) {
				body.source = source;
			}
			this.requestService.requestPost(url, body, () => resolve(), (err) => reject(err));
		});
	}
	// Edit a standard name
	editStandard_location(cateId, standardId: number, standardName: string, subCates?: string[], parentId?: number): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'admin/nagNamesStandardLocation';
			const body: any = {
				nctId: cateId,
				stnId: standardId,
				stnName: standardName.toUpperCase(),
				subcates: JSON.stringify(subCates),
				customerId: this.userService.getCustomerIdByType()
			};
			if (parentId || parentId === 0) {
				body.stnParentId = parentId;
			}
			this.requestService.requestPut(url, body, () => resolve(), (err) => reject(err));
		});
	}
	// Edit a standard name
	editStandard_street(cateId, standardId: number, standardName: string, source: string): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'admin/nagNamesStandardStreet';
			const body: any = {
				nctId: cateId,
				stnId: standardId,
				stnName: standardName,
				source: source,
				customerId: this.userService.getCustomerIdByType()
			};
			this.requestService.requestPut(url, body, () => resolve(), (err) => reject(err));
		});
	}
	// Remove a standard name
	deleteStandard(cateId, standardId: number): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'admin/nagNamesStandard';
			const body = {
				nctId: cateId,
				stnId: standardId,
				customerId: this.userService.getCustomerIdByType()
			};
			this.requestService.requestDelete(url, {
				body: body
			}, () => resolve(), (err) => reject(err));
		});
	}
	// Add an alias
	addAlias(cateId, standardId: number, aliasName: string): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'admin/nagNamesAlias';
			const body: any = {
				nctId: cateId,
				stnId: standardId,
				alnName: aliasName,
				customerId: this.userService.getCustomerIdByType()
			};
			this.requestService.requestPost(url, body, () => resolve(), (err) => reject(err));
		});
	}
	// Edit an alias
	editAlias(cateId, aliasId: number, aliasName: string): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'admin/nagNamesAlias';
			const body = {
				alnId: aliasId,
				alnName: aliasName,
				nctId: cateId,
				customerId: this.userService.getCustomerIdByType()
			};
			this.requestService.requestPut(url, body, (rsp) => resolve(), (err) => reject(err));
		});
	}
	// Remove an alias
	deleteAlias(aliasId: number): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'admin/nagNamesAlias';
			const body = {
				alnId: aliasId,
				customerId: this.userService.getCustomerIdByType()
			};
			this.requestService.requestDelete(url, {
				body: body
			}, () => resolve(), (err) => reject(err));
		});
	}
	updateMvStComponents() {
		return new Promise((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'a3i/mvStComponents';
			this.requestService.requestPut(url, {}, rsp => resolve(rsp), err => reject(err));
		});
	}

	getNagNamesStandardId(stnName: string, nctId, stateCode, countyName?: string) {
		return new Promise<{stnId; placeTypes?: string[]}[]>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'admin/nagNamesStdId';
			const params: any = {
				stnName: stnName.toUpperCase(),
				nctId: nctId.toString(),
				stateCode: stateCode,
				customerId: this.userService.getCustomerIdByType()
			};
			if (countyName) {
				params.countyName = countyName;
			}
			this.requestService.requestGet(url, {
				params: params
			}, (rsp) => {
				const list: {stnId; placeTypes: string[]}[] = [];
				if (rsp.data) {
					rsp.data.forEach(i => list.push({
						stnId: i.stn_id,
						placeTypes: i.place_types
					}))
				}
				resolve(list);
			}, (err) => {
				reject(err);
			});
		});
	}
}
